/**
 * Decision 0144 (experiment 11, 0136 §3): a card in the one shape, wherever it is read outside Needs you: a ref chip's
 * popover, a brief entry, a bell row. In order: the question, why it matters, the recommendation and its reason
 * (above the answers), each answer with what happens next, who it waits on, details folded. A card decided in place
 * shows its answers; one with a lot around it (big plans, outward actions, permission prompts, reviews with
 * artifacts) leads to its page instead. After an answer, the result shows in place.
 */
import type { CardAnswer, CardShape } from "@minga/protocol"
import { type FormEvent, type KeyboardEvent, type ReactNode, useEffect, useId, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { fieldClass } from "@/components/ui/field"
import { cn } from "@/lib/utils"
import { whenText } from "@/model/glossary"
import type { RefDecide } from "../markdown/refs"
import { Glyph } from "./ref-icons"

export type CardShapeLayout = "popover" | "entry" | "row"

/** "Lea recommends Approve." / "Lea:" (a recommendation that names no answer). */
export const recommendationLead = (r: NonNullable<CardShape["recommendation"]>): string =>
  r.label ? `${r.by ?? "Minga"} recommends ${r.label}.` : `${r.by ?? "Minga"}:`

/** "Waiting on you since Sun 12:12 · Blocks 10 items". */
export const waitsLine = (shape: CardShape, now: number): string =>
  [
    `Waiting on ${shape.waitsOn} since ${whenText(shape.since, now)}`,
    shape.blocks > 0 ? `Blocks ${shape.blocks} ${shape.blocks === 1 ? "item" : "items"}` : null,
  ]
    .filter(Boolean)
    .join(" · ")

/** Move focus between a group's answer buttons with the arrow keys (0136 §B: arrow keys between answers). */
const onArrows = (event: KeyboardEvent<HTMLElement>) => {
  if (!["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(event.key)) return
  const buttons = [...event.currentTarget.querySelectorAll<HTMLElement>("[data-answer]")]
  if (buttons.length === 0) return
  const at = buttons.indexOf(document.activeElement as HTMLElement)
  if (at < 0) return
  event.preventDefault()
  const step = event.key === "ArrowDown" || event.key === "ArrowRight" ? 1 : -1
  buttons[(at + step + buttons.length) % buttons.length]?.focus()
}

export function CardShapeView({
  shape: initial,
  decide,
  layout,
  openHref,
  onOpen,
  autoFocus = false,
  now = Date.now(),
  askExtra,
}: {
  shape: CardShape
  /** How an answer is sent here; null: read-only (the answers show, the page decides). */
  decide: RefDecide | null
  layout: CardShapeLayout
  /** The card's page (a real link). */
  openHref?: string | undefined
  /** Open the page in place (the router); defaults to following `openHref`. */
  onOpen?: (() => void) | undefined
  /** Focus the first answer when shown (the popover). */
  autoFocus?: boolean
  now?: number
  /** After the question (the chip of the card in a brief entry). */
  askExtra?: ReactNode
}) {
  const [shape, setShape] = useState(initial)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [noting, setNoting] = useState<CardAnswer | null>(null)
  const [note, setNote] = useState("")
  const [result, setResult] = useState<string | null>(null)
  const answersRef = useRef<HTMLFieldSetElement>(null)
  const noteId = useId()
  useEffect(() => setShape(initial), [initial])
  useEffect(() => {
    if (autoFocus) answersRef.current?.querySelector<HTMLElement>("[data-answer]")?.focus()
  }, [autoFocus])

  const send = async (answer: CardAnswer, text: string | null) => {
    if (!decide) return
    const decision =
      answer.key === "request_revision"
        ? { _tag: "request_revision" as const, note: text ?? "" }
        : answer.key === "reject" && text
          ? { _tag: "reject" as const, reason: text }
          : answer.decision
    if (!decision) return
    setBusy(true)
    setError(null)
    try {
      const next = await decide(shape, decision)
      if (next) setShape(next)
      setResult(
        next?.decided?.line ??
          (answer.key === "request_revision" ? "Sent back with your note" : `${answer.label}: sent`),
      )
      setNoting(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const choose = (answer: CardAnswer) => {
    if (answer.note === "required" || (answer.destructive && answer.note === "optional")) {
      setNoting(answer)
      setNote("")
      return
    }
    void send(answer, null)
  }

  const submitNote = (event: FormEvent) => {
    event.preventDefault()
    if (!noting) return
    if (noting.note === "required" && !note.trim()) return
    void send(noting, note.trim() || null)
  }

  const entry = layout !== "popover"
  const decidable = shape.open && shape.inPlace && decide !== null && result === null
  const r = shape.recommendation

  return (
    <div data-card-shape={shape.ref} className="flex min-w-0 flex-col gap-2.5">
      <p
        className={cn(
          entry ? "text-[14.5px] font-semibold text-ink" : "type-card text-[19px] leading-snug text-ink",
        )}
      >
        {shape.open ? shape.ask : <s className="decoration-faint">{shape.ask}</s>}
        {askExtra && <> {askExtra}</>}
      </p>
      {shape.why && layout === "popover" && (
        <p className="line-clamp-3 text-[14px] whitespace-pre-wrap text-ink-2">{shape.why}</p>
      )}
      {r && shape.open && (
        <p
          data-recommendation=""
          className={cn(
            "rounded-r-control border-l-2 border-needs bg-needs-soft text-ink-2",
            entry ? "px-2.5 py-1.5 text-[13px]" : "px-3 py-2 text-[14px]",
          )}
        >
          <span className="font-semibold text-needs-deep">{recommendationLead(r)}</span> {r.reason}
        </p>
      )}
      {decidable && noting === null && (
        <fieldset
          ref={answersRef}
          aria-label="Answers"
          onKeyDown={onArrows}
          className={cn("m-0 min-w-0 border-0 p-0", entry ? "flex flex-wrap gap-1.5" : "grid gap-2")}
        >
          {shape.answers.map((a) =>
            entry ? (
              <Button
                key={a.key}
                data-answer={a.key}
                size="sm"
                variant={a.recommended ? "decision" : a.destructive ? "destructive" : "secondary"}
                disabled={busy}
                title={a.outcome ?? undefined}
                onClick={() => choose(a)}
              >
                {a.label}
              </Button>
            ) : (
              <button
                key={a.key}
                type="button"
                data-answer={a.key}
                disabled={busy}
                onClick={() => choose(a)}
                className={cn(
                  "tap grid w-full grid-cols-[auto_1fr] items-start gap-x-3 gap-y-1 rounded-control border px-3 py-2.5 text-left",
                  "disabled:opacity-60 max-sm:grid-cols-1",
                  a.recommended
                    ? "border-needs bg-needs-soft hover:bg-needs-soft"
                    : "border-line-control bg-surface hover:bg-fill-soft",
                )}
              >
                <span
                  className={cn(
                    "text-[14px] font-semibold whitespace-nowrap",
                    a.recommended ? "text-needs-deep" : a.destructive ? "text-blocked" : "text-ink",
                  )}
                >
                  {a.label}
                  {a.recommended && <span className="kicker ml-1.5 text-needs">recommended</span>}
                </span>
                {a.outcome && <span className="text-[13px] text-ink-2">{a.outcome}</span>}
              </button>
            ),
          )}
        </fieldset>
      )}
      {decidable && noting && (
        <form className="flex flex-col gap-1.5" onSubmit={submitNote}>
          <label className="type-secondary text-ink-2" htmlFor={noteId}>
            {noting.note === "required" ? "What should change?" : "Why not? (optional)"}
          </label>
          <textarea
            id={noteId}
            // biome-ignore lint/a11y/noAutofocus: the note is the next step after choosing this answer
            autoFocus
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={cn(fieldClass, "w-full")}
          />
          <div className="flex flex-wrap gap-1.5">
            <Button
              type="submit"
              size="sm"
              variant={noting.destructive ? "destructive" : "primary"}
              disabled={busy || (noting.note === "required" && !note.trim())}
            >
              {noting.note === "required" ? "Send back" : noting.label}
            </Button>
            <Button size="sm" variant="quiet" onClick={() => setNoting(null)}>
              Never mind
            </Button>
          </div>
        </form>
      )}
      {shape.open && !shape.inPlace && result === null && (
        <p className="flex flex-wrap items-center gap-2 text-[13px] text-ink-2">
          <span>There is a lot around this card: decide it on its page.</span>
          {openHref && (
            <a
              href={openHref}
              className="tap font-semibold text-ink"
              onClick={(event) => {
                if (!onOpen || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
                event.preventDefault()
                onOpen()
              }}
            >
              Open the card <Glyph name="open" size={11} className="inline align-[-1px]" />
            </a>
          )}
        </p>
      )}
      {(result ?? (!shape.open ? shape.decided?.line : null)) && (
        <p role="status" className="text-[13px] font-semibold text-live">
          {result ?? shape.decided?.line}
          {shape.decisionRef && (
            <span className="ml-1.5 font-mono text-[12px] font-normal text-muted">{shape.decisionRef}</span>
          )}
        </p>
      )}
      {error && (
        <p role="alert" className="text-[13px] text-blocked">
          {error}
        </p>
      )}
      {shape.open && layout !== "row" && <p className="text-[12.5px] text-muted">{waitsLine(shape, now)}</p>}
      {shape.details && layout === "popover" && (
        <details className="text-[13px]">
          <summary className="tap cursor-pointer text-muted hover:text-ink">Details</summary>
          <p className="mt-2 border-l border-rule pl-3 whitespace-pre-wrap text-ink-2">{shape.details}</p>
        </details>
      )}
    </div>
  )
}
